// @flow
import styled from '@emotion/styled';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane';
import { SPACING, typography } from '@tablekit/theme';
import Icon from '@tablekit/icon';
import CTAButton from 'components/CTAButton';
import Link from 'components/Link';
import { getPageContext } from 'components/PageContext';
import RichText from 'components/RichText';
import CoreTitle from 'styled/Title';
import columnWidth from 'utils/columnWidth';
import type { BlokComponentPropsType, DocType } from '../types';

const WIDTH = columnWidth(10);

const Wrapper = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: ${columnWidth(12)}%;
  width: ${columnWidth(12)}%;
  min-width: ${WIDTH}%;

  @media (min-width: 768px) {
    max-width: ${WIDTH}%;
    width: ${WIDTH}%;
  }
`;

const Title = styled(CoreTitle)`
  cursor: pointer;
`;

const ApplyIcon = styled(Icon)`
  margin-right: ${SPACING.L2};
`;

const Description = styled(RichText)`
  margin-bottom: ${SPACING.L4};

  ${typography('L3')};

  h4 {
    ${typography('L5')};
    margin-bottom: ${SPACING.L3};
  }

  p,
  ul {
    margin-bottom: ${SPACING.L5};
  }

  ul {
    list-style-type: disc;
    list-style-position: outside;

    li {
      margin-left: ${SPACING.L5};
      padding-left: ${SPACING.L4};
    }

    p {
      display: inline;
      margin-bottom: 0;
    }
    p:not(:last-child) {
      margin-bottom: ${SPACING.L1};
    }
  }
`;

const TitleJumpLink = styled.a`
  &,
  &:hover,
  &:active,
  &:visited {
    color: currentColor;
    text-decoration: none;
  }
`;

const Anchor = styled.a`
  position: absolute;
  // this negative spacing means that we scroll to the correct place on the page
  top: -${SPACING.L8};
`;

export default function Job({
  blok: { title = '', description }
}: BlokComponentPropsType<{
  description: DocType,
  title: string
}>) {
  const { jobsConfig } = getPageContext();
  const { apply_modal: applyModal, apply_text: applyText } = jobsConfig;
  const bookmark = encodeURIComponent(title.toLowerCase());
  return (
    <Wrapper>
      <Anchor id={bookmark} />
      <Title align="left">
        <TitleJumpLink href={`#${bookmark}`}>{title}</TitleJumpLink>
      </Title>
      <Description richText={description} />
      <CTAButton
        component={Link}
        gaReference="opened job application"
        link={applyModal}
        modalDynamicOptions={{
          job_title: title
        }}
      >
        <ApplyIcon icon={faPaperPlane} />
        {applyText}
      </CTAButton>
    </Wrapper>
  );
}
